"use client";

import React from "react";
import { Control, Controller } from "react-hook-form";
import {
  Box, FormControl,
  FormControlLabel, FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography, useMediaQuery
} from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import isEmail from "@/utils/validation/is-email-util";
import { validatePhoneByLocale } from "@/utils/validation/verify-phone-by-locale";
import { ColletteCheckbox, ColletteButton } from "@collette-travel/collette-common-controls";
import { Locale } from "@/i18n/routing";
import ManagedReCAPTCHA from "@/components/managed-recaptcha/managed-recaptcha";
import {RAQFormData} from "@/types/leads/lead-forms";

interface RAQFormProps {
  control: Control<RAQFormData, any>;
  handleSubmit: React.FormEventHandler<HTMLFormElement> | undefined;
  isInvalid: boolean;
  isSubmitting: boolean;
}

const RequestAQuoteForm = ({ control, handleSubmit, isInvalid, isSubmitting }: RAQFormProps) => {
  const t = useTranslations("FORMS");
  const locale = useLocale() as Locale;
  const shrinkReCaptcha = useMediaQuery("(max-width:425px)");

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      data-testid="raq-form"
      data-form-name="Request a Quote"
      data-form-id="request-a-quote-form"
      data-track="visibility"
    >
      <Stack
        sx={{
          flexDirection: "column",
          gap: "1.6rem",
          padding: { xs: "2rem 2.4rem", md: "2.4rem 4rem" }
        }}
      >
        <Typography component="p" variant="h6" data-testid="raq-contact-section-header">
          {t("Contact")}
        </Typography>
        <Stack
          component="fieldset"
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: "1.6rem", sm: "3.2rem" },
            border: "none",
            padding: 0
          }}
        >
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: t("Please complete this required field")
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                data-testid="raq-first-name-field"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("First Name")}
                placeholder={t("First Name")}
                label={t("First Name")}
                sx={{
                  width: { xs: "100%", sm: "50%" }
                }}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{
              required: t("Please complete this required field")
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                data-testid="raq-last-name-field"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("Last Name")}
                placeholder={t("Last Name")}
                label={t("Last Name")}
                sx={{
                  width: { xs: "100%", sm: "50%" }
                }}
              />
            )}
          />
        </Stack>
        <Stack
          component="fieldset"
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: "1.6rem", sm: "3.2rem" },
            border: "none",
            padding: 0
          }}
        >
          <Controller
            name="email"
            control={control}
            rules={{
              required: t("Please complete this required field"),
              validate: (value) => isEmail(value) || t("Email must be formatted correctly")
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                data-testid="raq-email-field"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("Email")}
                placeholder={t("Email")}
                label={t("Email")}
                sx={{
                  width: { xs: "100%", sm: "50%" }
                }}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              required: t("Please complete this required field"),
              validate: (value) => validatePhoneByLocale(value, locale, t)
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                data-testid="raq-phone-field"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("Phone")}
                placeholder={t("Phone")}
                label={t("Phone")}
                sx={{
                  width: { xs: "100%", sm: "50%" }
                }}
              />
            )}
          />
        </Stack>
        <Controller
          name="isTravelAgent"
          control={control}
          rules={{
            required: t("Please complete this required field")
          }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              <Stack
                sx={{
                  gap: "1.2rem",
                  alignItems: {xs: "flex-start", sm: "center"},
                  flexDirection: {xs: "column", sm: "row"}
                }}
              >
                <Typography
                  sx={{
                    "&::after": { content: "\"*\"", ariaHidden: "true" }
                  }}
                >
                  {t("Are you a travel advisor?")}
                </Typography>
                <RadioGroup
                  {...field}
                  value={field.value ?? ""}
                  onChange={(_, value) => field.onChange(value)}
                  row
                  aria-label={t("Are you a travel advisor?")}
                  data-testid="raq-travel-advisor-radio-group"
                  sx={{
                    gap: "3.2rem"
                  }}
                >
                  <FormControlLabel
                    sx={{
                      margin: 0
                    }}
                    control={
                      <Radio
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: "primary.main",
                            fill: "primary.main"
                          }
                        }}
                      />
                    }
                    label={t("Yes")}
                    value="yes"
                  />
                  <FormControlLabel
                    sx={{
                      margin: 0
                    }}
                    control={
                      <Radio
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: "primary.main",
                            fill: "primary.main"
                          }
                        }}
                      />
                    }
                    label={t("No")}
                    value="no"
                  />
                </RadioGroup>
              </Stack>
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Stack gap={1}>
          <Controller
            name="optInEmail"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="raq-opt-in-email-checkbox-field"
                label={t("Email opt in")}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
          <Controller
            name="workingWithAgent"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="raq-working-with-agent-checkbox-field"
                label={t("Working with travel professional")}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
          <Controller
            name="withMoreTravelers"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="raq-with-more-travelers-checkbox-field"
                label={t("Traveling with 8 or more people")}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </Stack>
        <Controller
          name="recaptchaToken"
          control={control}
          rules={{
            required: t("Please complete this required field")
          }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              <Box
                sx={{
                  transformOrigin: "0 0",
                  transform: `scale(${shrinkReCaptcha ? "0.7" : "1" })`
                }}
              >
                <ManagedReCAPTCHA
                  size="normal"
                  sitekey="6LfV8zIpAAAAALFDQIb2-f4xUO3PrI8vVhrgwA9x"
                  onChange={(token) => field.onChange(token)}
                />
              </Box>
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* VALIDATION MESSAGE */}
        {isInvalid && (
          <Box
            sx={{
              padding: "0.4rem 1rem",
              backgroundColor: "error.100",
              borderRadius: "1rem",
              color: "error.dark"
            }}
          >
            <Typography>
              {t("Please complete all required fields")}
            </Typography>
          </Box>
        )}

        <Box width="100%" display="flex" justifyContent="end">
          <ColletteButton
            disabled={isSubmitting}
            label={t("Request Information")}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            testId="raq-submit-button"
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default RequestAQuoteForm;
